import { Component, Element, Event, EventEmitter, h, Host, Prop, Watch } from "@stencil/core";

const baseClass = "ux-checkbox";
let id = 0;

/**
 * @slot - Can be used for Label instead of the label Prop.
 */
@Component({
    tag: "ux-checkbox",
    styleUrl: "checkbox.scss",
    shadow: false
})
export class Checkbox {
    /** value of the input element, not visible to the user */
    @Prop() readonly value: string = "";
    /** text inside the checkbox label element, visible to the user */
    @Prop() readonly label: string = "";
    /** Input id attribute */
    @Prop({ mutable: true, reflect: true }) inputId?: string;
    /** name which is used for checkbox groups or as identifier in forms */
    @Prop() readonly name: string = "";
    /** indicates if the checkbox is checked */
    @Prop({ mutable: true }) checked = false;
    /** indicates if the checkbox is disabled */
    @Prop() readonly disabled: boolean = false;
    /** indicates if the checkbox is required  */
    @Prop() readonly required: boolean = false;
    /** indicates if the checkbox has an error state  */
    @Prop() readonly error: boolean = false;
    /** Error message that is rendered if component has an error */
    @Prop() readonly errorMsg: string | null = null;
    /** to be set if the checkbox should not have content inside the label */
    @Prop() readonly noLabel: boolean = false;

    /** the ux-checkbox element */
    @Element() element!: HTMLUxCheckboxElement;
    /** the checkbox input element */
    private checkboxInputElement: HTMLInputElement;

    @Watch("inputId")
    setInputId() {
        this.inputId = this.inputId || `${baseClass}-${id++}`;
    }

    componentWillLoad() {
        this.setInputId();
    }

    /** checkbox event, includes checked status, inputId and input name */
    @Event({
        eventName: "uxCheckboxChange",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    checkboxEvent!: EventEmitter<{
        name: string;
        value: string;
        inputId: string;
        checked: boolean;
    }>;

    private onChange = (event: Event) => {
        if (event.target !== this.checkboxInputElement) {
            return;
        }
        this.checked = this.checkboxInputElement?.checked;
        this.checkboxEvent.emit({
            checked: this.checkboxInputElement?.checked,
            // @ts-expect-error Check for typing
            inputId: this.inputId,
            name: this.name,
            value: this.value
        });
    };

    render() {
        const { value, label, inputId, name, checked, disabled, required, error, errorMsg, noLabel } = this;

        // baseClass is not used for Host element because it is also a static component
        return (
            <Host>
                <label
                    class={{
                        [baseClass]: true,
                        [`${baseClass}--disabled`]: disabled,
                        [`${baseClass}--error`]: error
                    }}
                >
                    <input
                        class={`${baseClass}__input`}
                        type="checkbox"
                        id={inputId}
                        name={name}
                        value={value}
                        checked={checked}
                        disabled={disabled}
                        required={required}
                        // @ts-expect-error Check for typing
                        ref={(el) => (this.checkboxInputElement = el)}
                        onChange={this.onChange}
                        aria-invalid={error ? "true" : "false"}
                    />
                    <span
                        class={{
                            [`${baseClass}__label`]: true,
                            [`${baseClass}__label--error`]: error && errorMsg === "",
                            [`${baseClass}__label--empty`]: noLabel
                        }}
                    >
                        {!noLabel ? label : null}
                        <span class={`${baseClass}__slot-container`}>
                            <slot />
                        </span>
                        {required && <span> *</span>}
                        {error && errorMsg !== "" && <span class="ux-label__error-message">{errorMsg}</span>}
                    </span>
                </label>
            </Host>
        );
    }
}
